import { useState } from 'react';
import { CheckCircle, Zap, Star, Rocket, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { BillingCheckout } from './BillingCheckout';
import { useStartTrial, usePlans } from '../src/hooks/useBilling';
import { SubscriptionPlan } from '../src/services/billing.service';

interface Props {
  onTrialStarted: () => void;
}

function formatStorageMb(mb: number): string {
  if (mb === 0) return 'Unlimited storage';
  if (mb >= 1024) return `${Math.round(mb / 1024)} GB storage`;
  return `${mb} MB storage`;
}

function formatLimit(n: number, singular: string, plural?: string): string {
  if (n === 0) return `Unlimited ${plural ?? singular}`;
  return `Up to ${n.toLocaleString()} ${n === 1 ? singular : (plural ?? singular)}`;
}

function getPlanFeatures(plan: SubscriptionPlan): string[] {
  const bullets: string[] = [];
  if (plan.max_users != null) bullets.push(formatLimit(plan.max_users, 'user', 'users'));
  if (plan.max_projects != null) bullets.push(formatLimit(plan.max_projects, 'project', 'projects'));
  if (plan.storage_limit_mb != null) bullets.push(formatStorageMb(plan.storage_limit_mb));
  if (Array.isArray(plan.features)) bullets.push(...plan.features);
  return bullets;
}

export function PlanSelectionGate({ onTrialStarted }: Props) {
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutPlanId, setCheckoutPlanId] = useState<string | undefined>();
  const { mutateAsync: startTrial, isPending, isError, error } = useStartTrial();
  const { data: plans = [], isLoading: plansLoading } = usePlans();

  const starterPlan = plans.find(
    (p) => p.tier === 'STARTER' || p.display_name.toLowerCase().includes('starter'),
  );
  const professionalPlan = plans.find(
    (p) =>
      p.tier === 'PROFESSIONAL' ||
      p.display_name.toLowerCase().includes('professional') ||
      p.display_name.toLowerCase().includes('pro'),
  );

  const starterFeatures = starterPlan ? getPlanFeatures(starterPlan) : [];
  const trialFeatures = professionalPlan ? getPlanFeatures(professionalPlan) : [];
  const proFeatures = professionalPlan ? getPlanFeatures(professionalPlan) : [];

  const starterSetupFee = starterPlan?.price_monthly ?? 0;
  const starterPriceLabel =
    starterSetupFee > 0
      ? `${starterPlan!.currency} ${starterSetupFee.toLocaleString()}`
      : 'Free';
  const starterSubLabel = starterSetupFee > 0 ? 'one-time setup fee' : 'free forever';

  const openCheckout = (planId?: string) => {
    setCheckoutPlanId(planId);
    setShowCheckout(true);
  };

  const handleStartTrial = async () => {
    try {
      await startTrial();
      onTrialStarted();
    } catch {
      // error displayed inline via isError
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-center bg-background/95 backdrop-blur-sm overflow-y-auto">
        <div className="max-w-3xl w-full mx-4 py-12">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Rocket className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h1 className="text-3xl font-bold mb-2">Welcome to OptiBuild</h1>
            <p className="text-muted-foreground text-base">
              Choose how you'd like to get started — you can always upgrade later.
            </p>
          </div>

          {/* Plan cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            {/* Free Trial */}
            <div className="relative rounded-xl border-2 border-primary bg-primary/5 p-6 flex flex-col">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                <Badge className="px-3 py-0.5 text-xs">Recommended</Badge>
              </div>
              <div className="flex justify-center mb-4">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Star className="h-6 w-6 text-primary" />
                </div>
              </div>
              <h2 className="text-lg font-semibold text-center mb-1">Free Trial</h2>
              <div className="text-center mb-5">
                <p className="text-3xl font-bold">14 days</p>
                <p className="text-sm text-muted-foreground">No credit card required</p>
              </div>
              <ul className="space-y-2 mb-6 flex-1 text-sm">
                {plansLoading ? (
                  <li className="flex justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </li>
                ) : trialFeatures.length > 0 ? (
                  trialFeatures.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                      {f}
                    </li>
                  ))
                ) : (
                  ['Full platform access', 'All modules unlocked', 'Priority support'].map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                      {f}
                    </li>
                  ))
                )}
              </ul>
              {isError && (
                <div className="flex items-center gap-1.5 text-xs text-destructive mb-3">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  {(error as any)?.message ?? 'Failed to start trial. Please try again.'}
                </div>
              )}
              <Button className="w-full gap-2" onClick={handleStartTrial} disabled={isPending}>
                {isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin" />Starting…</>
                ) : (
                  <><Star className="h-4 w-4" />Start Free Trial</>
                )}
              </Button>
            </div>

            {/* Starter */}
            <div className="rounded-xl border-2 border-border p-6 flex flex-col hover:border-primary/40 transition-colors">
              <div className="flex justify-center mb-4">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <Zap className="h-6 w-6 text-muted-foreground" />
                </div>
              </div>
              <h2 className="text-lg font-semibold text-center mb-1">
                {starterPlan?.display_name ?? 'Starter'}
              </h2>
              <div className="text-center mb-5">
                <p className="text-3xl font-bold">{starterPriceLabel}</p>
                <p className="text-sm text-muted-foreground">{starterSubLabel}</p>
              </div>
              <ul className="space-y-2 mb-6 flex-1 text-sm">
                {plansLoading ? (
                  <li className="flex justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </li>
                ) : starterFeatures.length > 0 ? (
                  starterFeatures.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                      {f}
                    </li>
                  ))
                ) : (
                  <li className="text-sm text-muted-foreground text-center py-2">
                    Contact us for details
                  </li>
                )}
              </ul>
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => openCheckout(starterPlan?.plan_id)}
              >
                <Zap className="h-4 w-4" />
                {starterSetupFee > 0 ? 'Pay Setup Fee' : 'Get Started Free'}
              </Button>
            </div>

            {/* Pro / Enterprise */}
            <div className="rounded-xl border-2 border-border p-6 flex flex-col hover:border-primary/40 transition-colors">
              <div className="flex justify-center mb-4">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <Rocket className="h-6 w-6 text-muted-foreground" />
                </div>
              </div>
              <h2 className="text-lg font-semibold text-center mb-1">Pro / Enterprise</h2>
              <div className="text-center mb-5">
                <p className="text-lg font-semibold">Flexible pricing</p>
                <p className="text-sm text-muted-foreground">monthly or annual billing</p>
              </div>
              <ul className="space-y-2 mb-6 flex-1 text-sm">
                {plansLoading ? (
                  <li className="flex justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </li>
                ) : proFeatures.length > 0 ? (
                  proFeatures.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                      {f}
                    </li>
                  ))
                ) : (
                  ['Unlimited projects & users', 'All modules + integrations', 'Dedicated support & SLA'].map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                      {f}
                    </li>
                  ))
                )}
              </ul>
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => openCheckout(professionalPlan?.plan_id)}
              >
                <Rocket className="h-4 w-4" />
                Choose Plan
              </Button>
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground">
            By continuing you agree to our{' '}
            <a
              href="https://optibuild.pro/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://optibuild.pro/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Privacy Policy
            </a>
            . Need help?{' '}
            <a href="mailto:support@optibuild.pro" className="text-primary hover:underline">
              Contact support
            </a>
          </p>
        </div>
      </div>

      <BillingCheckout
        open={showCheckout}
        onClose={() => setShowCheckout(false)}
        initialPlanId={checkoutPlanId}
      />
    </>
  );
}
